/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/QuickAddStep.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                    |
 * |---|------------------------|-------------------|------------------|---------------------------------|
 * | 1 | 2026-06-19             | Claude Sonnet 4.6 | Add New Step UX  | Created — inline step creator   |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<QuickAddStep />)
 *
 * EXPORTS
 * -------
 * QuickAddStep — inline step-creation row shown at bottom of step list
 *
 * UX FLOW
 * -------
 * 1. User types a command name (e.g. "tap", "screenshot") → dropdown filters
 * 2. Selects a command → relevant parameter fields appear
 * 3. For element commands: "Click on mirror" button enters capture mode
 *    → user taps the phone mirror → selector auto-fills
 * 4. "Add" button creates the step; "Cancel" dismisses the row
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Reuses LocatorField for all element-selector fields.
 * 2. Does NOT duplicate step-building logic — follows the same pattern as
 *    CommandToolbar.confirm() and StepEditForm.save().
 * 3. capture mode is managed by the caller (App.tsx) via RecorderV0.
 *
 * =============================================================================
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import type { IRStep, Selector, AssertionKind, PressKeyName } from "@mimiq/ir";
import { generateId } from "@mimiq/ir";
import { LocatorField } from "./LocatorField.js";
import { db } from "../db/index.js";

// ── Command catalogue ─────────────────────────────────────────────────────────

interface QuickCommand {
  label: string;
  description: string;
  type: string;
  needsElement: boolean;
  /** If true, checkpoint status defaults to "fail" */
  checkpointFail?: boolean;
}

const QUICK_COMMANDS: QuickCommand[] = [
  // Interactions
  { label: "Tap",               description: "Tap an element on screen",              type: "tap",               needsElement: true  },
  { label: "Double Tap",        description: "Double-tap an element",                 type: "doubleTap",         needsElement: true  },
  { label: "Long Press",        description: "Hold an element",                       type: "longPress",         needsElement: true  },
  { label: "Type Text",         description: "Enter text into focused field",         type: "typeText",          needsElement: false },
  { label: "Clear Text",        description: "Clear text from a field",               type: "clearText",         needsElement: true  },
  { label: "Swipe",             description: "Swipe in a direction",                  type: "swipe",             needsElement: false },
  { label: "Scroll To Element", description: "Scroll until an element is visible",   type: "scrollUntilVisible",needsElement: true  },
  // Keyboard
  { label: "Press Key",         description: "Press Back, Home, Enter, etc.",        type: "pressKey",          needsElement: false },
  { label: "Hide Keyboard",     description: "Dismiss the on-screen keyboard",       type: "hideKeyboard",      needsElement: false },
  // Wait
  { label: "Wait",              description: "Pause for a fixed number of ms",       type: "wait",              needsElement: false },
  { label: "Wait For Element",  description: "Wait until element is visible/hidden", type: "waitFor",           needsElement: true  },
  // Assert
  { label: "Assert Visible",    description: "Verify element is on screen",          type: "assert",            needsElement: true  },
  { label: "Screenshot",        description: "Capture a screenshot",                 type: "screenshot",        needsElement: false },
  // App
  { label: "Open App",          description: "Launch an app by package name",        type: "launchApp",         needsElement: false },
  { label: "Close App",         description: "Force-stop an app",                    type: "stopApp",           needsElement: false },
  { label: "Open URL",          description: "Navigate to a URL in the browser",     type: "openUrl",           needsElement: false },
  // Report
  { label: "Print Log",         description: "Add a message to the test report",     type: "print",             needsElement: false },
  { label: "Mark Pass",         description: "Place a PASS checkpoint in the report",type: "checkpoint",        needsElement: false },
  { label: "Mark Fail",         description: "Place a FAIL checkpoint in the report",type: "checkpoint",        needsElement: false, checkpointFail: true },
  { label: "Stop Test",         description: "Halt test execution immediately",       type: "stopTest",          needsElement: false },
  // Variables
  { label: "Set Variable",      description: "Store a value in a variable",              type: "setVariable",   needsElement: false },
  { label: "Get Variable",      description: "Print/log the current value of a variable",type: "getVariable",   needsElement: false },
  { label: "Random Value",      description: "Generate a random string/number into a variable", type: "randomValue", needsElement: false },
  { label: "Get Attribute",     description: "Read any element attribute (enabled, checked, resourceId, bounds…) into a variable", type: "getAttribute", needsElement: true },
  { label: "Script",            description: "Run a sandboxed JavaScript snippet — manipulate variables, do math, string ops", type: "script", needsElement: false },
  { label: "Call Function",    description: "Call a reusable function from the Function Library", type: "callFunction_qa", needsElement: false },
  { label: "Get Count",        description: "Count how many elements match a locator and store the count in a variable", type: "getCount", needsElement: true },
  // Control
  { label: "Break",             description: "Exit current loop (in Repeat/ForEach)",   type: "break",         needsElement: false },
  { label: "Continue",          description: "Skip to next loop iteration",              type: "continue",      needsElement: false },
  { label: "If Condition",      description: "Branch test based on element state",       type: "if",            needsElement: false },
  { label: "Repeat",            description: "Repeat a group of steps N times",          type: "repeat",        needsElement: false },
  { label: "For Each Row",      description: "Iterate over rows in a data source",       type: "forEachRow",    needsElement: false },
  { label: "API Call",          description: "Make an HTTP API request",                 type: "api",           needsElement: false },
  // Navigation
  { label: "Deep Link",         description: "Trigger an app deep link (e.g. myapp://path)", type: "deepLink", needsElement: false },
  // Gesture
  { label: "Hold & Scroll",     description: "Hold an element then drag to scroll",      type: "holdSwipe",     needsElement: true  },
  // Swipe shortcuts
  { label: "Swipe Up",          description: "Swipe up (scrolls content downward)",      type: "swipeUp",       needsElement: false },
  { label: "Swipe Down",        description: "Swipe down (scrolls content upward)",      type: "swipeDown",     needsElement: false },
  { label: "Swipe Left",        description: "Swipe left",                               type: "swipeLeft",     needsElement: false },
  { label: "Swipe Right",       description: "Swipe right",                              type: "swipeRight",    needsElement: false },
  // Device state
  { label: "Dark Mode On",      description: "Switch system UI to dark theme (Android 10+)", type: "darkModeOn",  needsElement: false },
  { label: "Dark Mode Off",     description: "Switch system UI to light theme",          type: "darkModeOff",   needsElement: false },
  { label: "Location On",       description: "Enable location services (high accuracy)", type: "locationOn",    needsElement: false },
  { label: "Location Off",      description: "Disable location services",                type: "locationOff",   needsElement: false },
  { label: "Show Keyboard",     description: "Show the soft keyboard (IME)",             type: "showKeyboard",  needsElement: false },
  { label: "WiFi On",           description: "Enable WiFi",                              type: "wifiOn",        needsElement: false },
  { label: "WiFi Off",          description: "Disable WiFi",                             type: "wifiOff",       needsElement: false },
  { label: "Airplane On",       description: "Enable airplane mode (disables all radios)", type: "airplaneOn",  needsElement: false },
  { label: "Airplane Off",      description: "Disable airplane mode",                    type: "airplaneOff",   needsElement: false },
];

// ── Props ─────────────────────────────────────────────────────────────────────

export interface QuickAddStepProps {
  onAdd:          (step: IRStep) => void;
  onCancel:       () => void;
  onStartCapture: (callback: (sel: Selector) => void) => void;
  onStopCapture:  () => void;
  capturing:      boolean;
  adb?:           import("@yume-chan/adb").Adb | null; // for package auto-suggest in Open/Close App
}

// ── Component ─────────────────────────────────────────────────────────────────

export function QuickAddStep({ onAdd, onCancel, onStartCapture, onStopCapture, capturing, adb }: QuickAddStepProps) {
  const sharedFunctions = useLiveQuery(() => db.sharedFlows.orderBy("name").toArray(), []) ?? [];
  const [packages, setPackages]         = useState<string[]>([]);
  const [pkgLoading, setPkgLoading]     = useState(false);
  const pkgFetched = useRef(false);
  const [query, setQuery]             = useState("");
  const [selected, setSelected]       = useState<QuickCommand | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Element selector
  const [locator, setLocator] = useState<Selector>({ description: "element", candidates: [] });
  const [showManualLocator, setShowManualLocator] = useState(false);

  // Step-specific param state
  const [text, setText]               = useState("");
  const [durationMs, setDurationMs]   = useState(1000);
  const [pressKey, setPressKey]       = useState<PressKeyName>("back");
  const [waitState, setWaitState]     = useState<"visible"|"hidden"|"enabled"|"disabled">("visible");
  const [assertKind, setAssertKind]   = useState<AssertionKind>("visible");
  const [swipeDir, setSwipeDir]       = useState<"up"|"down"|"left"|"right">("down");
  const [pkg, setPkg]                 = useState("");
  const [url, setUrl]                 = useState("");
  const [printMsg, setPrintMsg]       = useState("");
  const [varName, setVarName]         = useState("");
  const [varValue, setVarValue]       = useState("");
  const [screenshotLabel, setScreenshotLabel] = useState("");
  const [deepLinkUrl, setDeepLinkUrl]         = useState("");
  const [scriptCode, setScriptCode]           = useState("// Access variables: const { vars } = context;\n// Return: { vars: { myVar: 'value' }, fail: 'optional error' }\nreturn { vars: {} };");
  const [cfQuery, setCfQuery]                 = useState("");
  const [cfPickedId, setCfPickedId]           = useState("");
  const [cfArgs, setCfArgs]                   = useState<Record<string, string>>({});
  const [attrName, setAttrName]               = useState("enabled");
  type A11yAttr = "text"|"resourceId"|"contentDesc"|"className"|"pkg"|"enabled"|"checked"|"clickable"|"focused"|"selected"|"scrollable"|"longClickable"|"password"|"checkable"|"boundsLeft"|"boundsTop"|"boundsRight"|"boundsBottom"|"boundsWidth"|"boundsHeight";
  const A11Y_ATTRS: A11yAttr[] = ["text","resourceId","contentDesc","className","pkg","enabled","checked","clickable","focused","selected","scrollable","longClickable","password","checkable","boundsLeft","boundsTop","boundsRight","boundsBottom","boundsWidth","boundsHeight"];
  const [holdDirection, setHoldDirection]     = useState<"up"|"down"|"left"|"right">("down");
  const [holdDurationMs, setHoldDurationMs]   = useState(800);

  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the search input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Filter commands by query (case-insensitive, matches label or description)
  const filtered = query.trim()
    ? QUICK_COMMANDS.filter((c) =>
        c.label.toLowerCase().includes(query.toLowerCase()) ||
        c.description.toLowerCase().includes(query.toLowerCase()) ||
        c.type.toLowerCase().includes(query.toLowerCase())
      )
    : QUICK_COMMANDS;

  const loadPackages = useCallback(async () => {
    if (pkgFetched.current || !adb) return;
    pkgFetched.current = true;
    setPkgLoading(true);
    try {
      const raw = await adb.subprocess.noneProtocol.spawnWaitText(["pm", "list", "packages"]);
      setPackages(raw.split("\n").map(l => l.trim()).filter(l => l.startsWith("package:")).map(l => l.slice("package:".length)).sort());
    } catch { /* ignore */ } finally { setPkgLoading(false); }
  }, [adb]);

  function selectCommand(cmd: QuickCommand) {
    setSelected(cmd);
    setQuery(cmd.label);
    setShowDropdown(false);
    setLocator({ description: "element", candidates: [] });
    onStopCapture();
    // Auto-load package list for Open/Close App
    if (cmd.type === "launchApp" || cmd.type === "stopApp") {
      void loadPackages();
    }
  }

  function handleCaptureClick() {
    onStartCapture((sel) => {
      setLocator(sel);
    });
  }

  function handleAdd() {
    if (!selected) return;
    const id = generateId();
    const type = selected.type;

    let step: IRStep;

    switch (type) {
      case "tap":
        if (locator.candidates.length === 0) return;
        step = { id, type: "tap", selector: locator } as IRStep;
        break;
      case "doubleTap":
        if (locator.candidates.length === 0) return;
        step = { id, type: "doubleTap", selector: locator } as IRStep;
        break;
      case "longPress":
        if (locator.candidates.length === 0) return;
        step = { id, type: "longPress", selector: locator, durationMs } as IRStep;
        break;
      case "clearText":
        if (locator.candidates.length === 0) return;
        step = { id, type: "clearText", selector: locator } as IRStep;
        break;
      case "waitFor":
        if (locator.candidates.length === 0) return;
        step = { id, type: "waitFor", selector: locator, state: waitState, timeoutMs: durationMs } as IRStep;
        break;
      case "scrollUntilVisible":
        if (locator.candidates.length === 0) return;
        step = { id, type: "scrollUntilVisible", selector: locator, direction: swipeDir, maxScrolls: 10 } as IRStep;
        break;
      case "assert":
        if (locator.candidates.length === 0) return;
        step = { id, type: "assert", selector: locator, assertion: assertKind } as IRStep;
        break;
      case "typeText":
        if (!text.trim()) return;
        step = { id, type: "typeText", text: text.trim() } as IRStep;
        break;
      case "wait":
        step = { id, type: "wait", durationMs: Math.max(1, durationMs) } as IRStep;
        break;
      case "pressKey":
        step = { id, type: "pressKey", key: pressKey } as IRStep;
        break;
      case "hideKeyboard":
        step = { id, type: "hideKeyboard" } as IRStep;
        break;
      case "screenshot":
        step = { id, type: "screenshot", ...(screenshotLabel.trim() ? { label: screenshotLabel.trim() } : {}) } as IRStep;
        break;
      case "launchApp":
        if (!pkg.trim()) return;
        step = { id, type: "launchApp", packageName: pkg.trim() } as IRStep;
        break;
      case "stopApp":
        if (!pkg.trim()) return;
        step = { id, type: "stopApp", packageName: pkg.trim() } as IRStep;
        break;
      case "openUrl":
        if (!url.trim()) return;
        step = { id, type: "openUrl", url: url.trim() } as IRStep;
        break;
      case "deepLink":
        if (!deepLinkUrl.trim()) return;
        step = { id, type: "deepLink", url: deepLinkUrl.trim() } as IRStep;
        break;
      case "script":
        step = { id, type: "script", code: scriptCode, language: "javascript" } as IRStep;
        break;
      case "callFunction_qa": {
        const fn = sharedFunctions.find(f => f.id === cfPickedId);
        if (!fn) return;
        step = { id, type: "callFlow", sharedFlowId: fn.id, sharedFlowName: fn.name, args: { ...cfArgs } } as IRStep;
        break;
      }
      case "getCount":
        if (locator.candidates.length === 0 || !varName.trim()) return;
        step = { id, type: "setVariable", name: varName.trim(), source: { kind: "count", selector: locator } } as IRStep;
        break;
      case "print":
        if (!printMsg.trim()) return;
        step = { id, type: "print", message: printMsg.trim(), level: "info" } as IRStep;
        break;
      case "checkpoint":
        if (!printMsg.trim()) return;
        step = { id, type: "checkpoint", message: printMsg.trim(), status: selected.checkpointFail ? "fail" : "pass" } as IRStep;
        break;
      case "stopTest":
        step = { id, type: "stopTest", message: printMsg.trim() || "Test stopped", status: "failed" } as IRStep;
        break;
      case "setVariable":
        if (!varName.trim()) return;
        step = { id, type: "setVariable", name: varName.trim(), source: { kind: "literal", value: varValue } } as IRStep;
        break;
      case "break":
        step = { id, type: "break" } as IRStep;
        break;
      case "continue":
        step = { id, type: "continue" } as IRStep;
        break;
      case "swipe":
        step = { id, type: "swipe", from: [540, swipeDir === "down" || swipeDir === "left" ? 1600 : 800], to: [540, swipeDir === "down" ? 800 : 1600], durationMs: 300 } as IRStep;
        break;
      // Swipe direction shortcuts
      case "swipeUp":    step = { id, type: "swipe", from: [540, 1600], to: [540, 800],  durationMs: 300 } as IRStep; break;
      case "swipeDown":  step = { id, type: "swipe", from: [540, 800],  to: [540, 1600], durationMs: 300 } as IRStep; break;
      case "swipeLeft":  step = { id, type: "swipe", from: [900, 1200], to: [180, 1200], durationMs: 300 } as IRStep; break;
      case "swipeRight": step = { id, type: "swipe", from: [180, 1200], to: [900, 1200], durationMs: 300 } as IRStep; break;
      // Device state shortcuts
      case "darkModeOn":  step = { id, type: "setDeviceState", darkMode: true }  as IRStep; break;
      case "darkModeOff": step = { id, type: "setDeviceState", darkMode: false } as IRStep; break;
      case "locationOn":  step = { id, type: "setDeviceState", location: true }  as IRStep; break;
      case "locationOff": step = { id, type: "setDeviceState", location: false } as IRStep; break;
      case "showKeyboard":step = { id, type: "setDeviceState", showKeyboard: true } as IRStep; break;
      case "wifiOn":      step = { id, type: "setDeviceState", wifi: true }      as IRStep; break;
      case "wifiOff":     step = { id, type: "setDeviceState", wifi: false }     as IRStep; break;
      case "airplaneOn":  step = { id, type: "setDeviceState", airplane: true }  as IRStep; break;
      case "airplaneOff": step = { id, type: "setDeviceState", airplane: false } as IRStep; break;
      // New commands
      case "getAttribute":
        if (locator.candidates.length === 0 || !varName.trim()) return;
        step = { id, type: "setVariable", name: varName.trim(), source: { kind: "attribute", selector: locator, attribute: attrName as A11yAttr } } as IRStep;
        break;
      case "getVariable":
        if (!varName.trim()) return;
        step = { id, type: "print", message: `${varName.trim()} = \${${varName.trim()}}`, level: "info" } as IRStep;
        break;
      case "randomValue":
        if (!varName.trim()) return;
        step = { id, type: "setVariable", name: varName.trim(), source: { kind: "random", randomType: "alphanumeric", length: 8 } } as IRStep;
        break;
      case "holdSwipe":
        if (locator.candidates.length === 0) return;
        step = { id, type: "holdSwipe", selector: locator, direction: holdDirection, holdMs: holdDurationMs, distance: 400 } as IRStep;
        break;
      // Control flow
      case "if":         step = { id, type: "if", condition: { selector: { description: "element", candidates: [] }, assertion: "visible" }, then: [] } as IRStep; break;
      case "repeat":     step = { id, type: "repeat", count: 3, steps: [] } as IRStep; break;
      case "forEachRow": step = { id, type: "forEachRow", dataSource: "data", variable: "row", steps: [] } as IRStep; break;
      case "api":        step = { id, type: "api", method: "GET", url: "https://" } as IRStep; break;
      default:
        return;
    }

    onStopCapture();
    onAdd(step);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      if (showDropdown) { setShowDropdown(false); return; }
      onStopCapture(); onCancel();
    }
    if (e.key === "ArrowDown" && showDropdown && filtered.length > 0) {
      e.preventDefault();
      selectCommand(filtered[0]!);
    }
    if (e.key === "Enter" && filtered.length === 1 && !selected) selectCommand(filtered[0]!);
    if (e.key === "Enter" && selected) handleAdd();
  }

  // callFunction_qa doesn't need a canvas element; getCount does
  const needsElement = selected?.type === "callFunction_qa" ? false : (selected?.needsElement ?? false);
  const hasCandidate = locator.candidates.length > 0;

  return (
    // NOTE: overflow must be visible so the dropdown can overflow the row boundary.
    // The border-radius is applied per-child instead of on this wrapper.
    <div className="quick-add-row">
      {/* ── Command search bar (always visible) ── */}
      <div className="quick-add-search">
        <input
          ref={inputRef}
          className="quick-add-input"
          placeholder="Type a command… e.g. tap, swipe, wait, screenshot"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); setSelected(null); onStopCapture(); }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          onKeyDown={handleKeyDown}
        />
        {/* Always-visible ✕ close button */}
        <button
          className="quick-add-close"
          onMouseDown={(e) => { e.preventDefault(); onStopCapture(); onCancel(); }}
          title="Close (Esc)"
          aria-label="Close"
        >✕</button>

        {/* Dropdown — rendered outside the row via portal-like absolute positioning */}
        {showDropdown && filtered.length > 0 && (
          <ul className="quick-add-dropdown">
            {filtered.slice(0, 12).map((cmd) => (
              <li
                key={cmd.label + cmd.type}
                className={`quick-add-option${selected?.label === cmd.label ? " quick-add-option--active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); selectCommand(cmd); }}
              >
                <span className="quick-add-option-label">{cmd.label}</span>
                <span className="quick-add-option-desc">{cmd.description}</span>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="quick-add-option-empty">No commands match "{query}"</li>
            )}
          </ul>
        )}
      </div>

      {/* ── Hint when no command selected yet ── */}
      {!selected && !showDropdown && query.trim() && (
        <p className="quick-add-hint-text">↑ Select a command from the list, or keep typing to filter</p>
      )}

      {/* ── Parameter fields (shown after command is selected) ── */}
      {selected && (
        <div className="quick-add-params">
          {/* Selected command badge */}
          <div className="quick-add-selected-badge">
            <span className="quick-add-selected-label">{selected.label}</span>
            <span className="quick-add-selected-desc">{selected.description}</span>
            <button className="quick-add-change-btn" onClick={() => { setSelected(null); setQuery(""); setShowDropdown(true); onStopCapture(); inputRef.current?.focus(); }}>
              Change
            </button>
          </div>

          {/* Element selector (for tap, waitFor, assert, etc.) */}
          {needsElement && (
            <div className="quick-add-element">
              {/* Prominent capture CTA */}
              {!hasCandidate && (
                <div className={`quick-add-capture-cta${capturing ? " quick-add-capture-cta--active" : ""}`}>
                  {capturing ? (
                    <>
                      <span className="quick-add-capture-dot" />
                      <strong>Now tap any element on the phone mirror</strong>
                      <button className="quick-add-capture-cancel" onMouseDown={(e) => { e.preventDefault(); onStopCapture(); }}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <span>No element selected —</span>
                      <button className="quick-add-capture-btn" onMouseDown={(e) => { e.preventDefault(); handleCaptureClick(); }}>
                        📱 Click here, then tap an element on the mirror
                      </button>
                    </>
                  )}
                </div>
              )}
              {hasCandidate && (
                <div className="quick-add-captured">
                  <span className="quick-add-captured-ok">✓ Element Captured</span>
                  <button className="quick-add-change-btn" onClick={() => {
                    setLocator({ description: "element", candidates: [] });
                    onStopCapture();
                  }}>Clear</button>
                </div>
              )}
              {/* Manual locator entry — secondary path */}
              <div className="quick-add-locator-manual-row">
                <button
                  className="quick-add-locator-link"
                  onClick={() => setShowManualLocator((v) => !v)}
                >
                  {showManualLocator ? "▴ Hide manual locator" : "Or enter locator manually"}
                </button>
              </div>
              {showManualLocator && (
                <LocatorField selector={locator} onChange={setLocator} showLabel={false} />
              )}
            </div>
          )}

          {/* Step-specific param fields */}
          {(selected.type === "wait" || selected.type === "longPress") && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">{selected.type === "longPress" ? "Hold (ms):" : "Duration (ms):"}</label>
              <input type="number" className="quick-add-field quick-add-field--short" min="1" value={durationMs}
                onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} />
            </div>
          )}
          {selected.type === "waitFor" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Wait for:</label>
              <select className="quick-add-field" value={waitState} onChange={(e) => setWaitState(e.target.value as typeof waitState)}>
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
              <label className="quick-add-label">Timeout (ms):</label>
              <input type="number" className="quick-add-field quick-add-field--short" min="1" value={durationMs}
                onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} />
            </div>
          )}
          {selected.type === "assert" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Condition:</label>
              <select className="quick-add-field" value={assertKind} onChange={(e) => setAssertKind(e.target.value as AssertionKind)}>
                <option value="visible">Is visible</option>
                <option value="notVisible">Is not visible</option>
                <option value="enabled">Is enabled</option>
                <option value="disabled">Is disabled</option>
                <option value="checked">Is checked</option>
                <option value="hasText">Has text</option>
              </select>
            </div>
          )}
          {selected.type === "scrollUntilVisible" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Direction:</label>
              <select className="quick-add-field" value={swipeDir} onChange={(e) => setSwipeDir(e.target.value as typeof swipeDir)}>
                <option value="down">Down</option>
                <option value="up">Up</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
            </div>
          )}
          {selected.type === "swipe" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Direction:</label>
              <select className="quick-add-field" value={swipeDir} onChange={(e) => setSwipeDir(e.target.value as typeof swipeDir)}>
                <option value="down">Scroll Down</option>
                <option value="up">Scroll Up</option>
                <option value="left">Swipe Left</option>
                <option value="right">Swipe Right</option>
              </select>
            </div>
          )}
          {selected.type === "typeText" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Text:</label>
              <input className="quick-add-field" placeholder="Text to type…" value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
            </div>
          )}
          {selected.type === "pressKey" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Key:</label>
              <select className="quick-add-field" value={pressKey} onChange={(e) => setPressKey(e.target.value as PressKeyName)}>
                {(["back","home","enter","tab","delete"] as PressKeyName[]).map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </div>
          )}
          {selected.type === "screenshot" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Label (optional):</label>
              <input className="quick-add-field" placeholder="e.g. Login screen" value={screenshotLabel}
                onChange={(e) => setScreenshotLabel(e.target.value)} />
            </div>
          )}
          {(selected.type === "launchApp" || selected.type === "stopApp") && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Package:</label>
              <input className="quick-add-field"
                placeholder={pkgLoading ? "Loading packages…" : "Search or type package name"}
                value={pkg} list="qa-pkg-list"
                onChange={(e) => setPkg(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
              <datalist id="qa-pkg-list">{packages.map(p => <option key={p} value={p} />)}</datalist>
              {packages.length > 0 && <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>{packages.length} apps</span>}
            </div>
          )}
          {selected.type === "openUrl" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">URL:</label>
              <input className="quick-add-field" placeholder="https://…" value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
            </div>
          )}
          {(selected.type === "print" || selected.type === "checkpoint" || selected.type === "stopTest") && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Message:</label>
              <input className="quick-add-field" placeholder="e.g. Login successful" value={printMsg}
                onChange={(e) => setPrintMsg(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
            </div>
          )}
          {selected.type === "setVariable" && (
            <>
              <div className="quick-add-field-row">
                <label className="quick-add-label">Name:</label>
                <input className="quick-add-field quick-add-field--short" placeholder="myVar" value={varName}
                  onChange={(e) => setVarName(e.target.value)} />
              </div>
              <div className="quick-add-field-row">
                <label className="quick-add-label">Value:</label>
                <input className="quick-add-field" placeholder='e.g. "hello" or 42' value={varValue}
                  onChange={(e) => setVarValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
              </div>
            </>
          )}
          {selected.type === "getAttribute" && (
            <>
              <div className="quick-add-field-row">
                <label className="quick-add-label">Attribute:</label>
                <select className="quick-add-field" value={attrName} onChange={(e) => setAttrName(e.target.value)}>
                  {A11Y_ATTRS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="quick-add-field-row">
                <label className="quick-add-label">Store in:</label>
                <input className="quick-add-field quick-add-field--short" placeholder="myVar" value={varName}
                  onChange={(e) => setVarName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
              </div>
              <p className="quick-add-label" style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: 2 }}>
                ⓘ Returns string. Booleans = "true"/"false". Bounds in pixels.
              </p>
            </>
          )}
          {selected.type === "getVariable" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Variable name:</label>
              <input className="quick-add-field quick-add-field--short" placeholder="myVar" value={varName}
                onChange={(e) => setVarName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
              {varName.trim() && <span className="quick-add-label" style={{color:"var(--color-text-muted)"}}>→ prints &ldquo;{varName.trim()} = ${"{" + varName.trim() + "}"}&rdquo;</span>}
            </div>
          )}
          {selected.type === "randomValue" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Store in:</label>
              <input className="quick-add-field quick-add-field--short" placeholder="randomVar" value={varName}
                onChange={(e) => setVarName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
              <span className="quick-add-label">alphanumeric · 8 chars</span>
            </div>
          )}
          {selected.type === "holdSwipe" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Scroll direction:</label>
              <select className="quick-add-field" value={holdDirection} onChange={(e) => setHoldDirection(e.target.value as typeof holdDirection)}>
                <option value="down">Down</option>
                <option value="up">Up</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
              <label className="quick-add-label">Hold (ms):</label>
              <input type="number" className="quick-add-field quick-add-field--short" min="200" value={holdDurationMs}
                onChange={(e) => setHoldDurationMs(parseInt(e.target.value) || 800)} />
            </div>
          )}
          {selected.type === "deepLink" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Deep link URL:</label>
              <input className="quick-add-field" placeholder="myapp://path/to/screen" value={deepLinkUrl}
                onChange={(e) => setDeepLinkUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
            </div>
          )}
          {selected.type === "callFunction_qa" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
              <div className="quick-add-field-row">
                <label className="quick-add-label">Search function:</label>
                <div style={{ position: "relative", flex: 1 }}>
                  <input className="quick-add-field" placeholder="Type to search…" value={cfQuery}
                    onChange={e => { setCfQuery(e.target.value); setCfPickedId(""); setCfArgs({}); }} autoFocus />
                  {cfQuery.trim() && !cfPickedId && (
                    <ul className="fn-picker-dropdown">
                      {sharedFunctions.filter(f => f.name.toLowerCase().includes(cfQuery.toLowerCase())).slice(0, 10).map(f => (
                        <li key={f.id}>
                          <button className="fn-picker-option"
                            onMouseDown={e => { e.preventDefault(); setCfPickedId(f.id); setCfQuery(f.name); setCfArgs({}); }}>
                            <span className="fn-picker-name">♻ {f.name}</span>
                            <span className="fn-picker-meta">{f.steps.length}s</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              {cfPickedId && (() => {
                const fn = sharedFunctions.find(f => f.id === cfPickedId);
                if (!fn || fn.parameters.length === 0) return null;
                return fn.parameters.map(p => (
                  <div key={p.name} className="quick-add-field-row">
                    <label className="quick-add-label">{p.name}:</label>
                    <input className="quick-add-field" placeholder={p.defaultValue ? `default: "${p.defaultValue}"` : `\${${p.name}}`}
                      value={cfArgs[p.name] ?? ""}
                      onChange={e => setCfArgs(prev => ({ ...prev, [p.name]: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
                  </div>
                ));
              })()}
              {sharedFunctions.length === 0 && (
                <p className="quick-add-label" style={{ color: "var(--color-warning)" }}>No functions yet — go to ♻ Functions tab to create one.</p>
              )}
            </div>
          )}
          {selected.type === "getCount" && (
            <div className="quick-add-field-row">
              <label className="quick-add-label">Store count in:</label>
              <input className="quick-add-field quick-add-field--short" placeholder="elementCount" value={varName}
                onChange={e => setVarName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
              <span className="quick-add-label" style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}>
                integer stored as string
              </span>
            </div>
          )}
          {selected.type === "script" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
              <label className="quick-add-label">JavaScript code:</label>
              <textarea
                className="quick-add-field"
                style={{ minHeight: 120, fontFamily: "monospace", fontSize: "0.78rem", resize: "vertical" }}
                value={scriptCode}
                onChange={(e) => setScriptCode(e.target.value)}
                spellCheck={false}
              />
              <p className="quick-add-label" style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}>
                ⓘ Sandbox: no fetch, no DOM. Access vars via context.vars. Return {`{ vars: {...}, fail: 'msg' }`}.
              </p>
            </div>
          )}

          {/* ── Add / Cancel ── */}
          <div className="quick-add-actions">
            <button
              className="btn btn--primary quick-add-confirm"
              onClick={handleAdd}
              disabled={needsElement && !hasCandidate}
              title={needsElement && !hasCandidate ? "Tap an element on the mirror first, or enter a locator manually" : "Add this step to the test"}
            >
              Add Step
            </button>
            <button className="btn btn--ghost quick-add-cancel" onClick={() => { onStopCapture(); onCancel(); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
